import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import config from '@/config';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import Loader from '@/shared-components/loader/Loader';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';
import { getSsrDiscoverBootstrap } from '@/utils/ssrDiscoverBootstrap';

import renderCollectionSection from './slider-types/collectionSectionRegistry';
import PollsQuizzesSlider from './slider-types/PollsQuizzesSlider';

import './index.css';

const NEWSPAPER_MASTHEAD = 'The Art Magazine';

function readInitialNewspaperSeoPayload() {
  const payload = getSsrDiscoverBootstrap('newspaper');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

export default function SuarteNewspaper() {
  const t = useTranslations('common.newspaper');
  const { state } = useStateHandler();
  const userId = state.user_session?._id;
  const [newspaperSeo, setNewspaperSeo] = useState(() => readInitialNewspaperSeoPayload());
  const { loading, fetchData } = useCache(
    'suarte_newspaper_shared_order',
    `${config.apis.api.url}/newspaper/false`,
    {
      includeSearchQueries: { userId },
      type: '@cache/dynamic',
    }
  );

  useEffect(() => {
    let ignore = false;

    async function loadNewspaperSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/discover/newspaper`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setNewspaperSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load newspaper SEO payload', error);
        }
      }
    }

    loadNewspaperSeo();

    return () => {
      ignore = true;
    };
  }, []);

  if (loading || !fetchData) {
    return (
      <AppNavigationPage>
        <div className="newspaper__loading-page">
          <Loader className="newspaper__loading-spinner" thin />
        </div>
      </AppNavigationPage>
    );
  }

  // Gather ALL polls/quizzes collections
  const pollsCollections   = fetchData.collections?.filter(c => c.type === 'newspaper-polls') ?? [];
  const quizzesCollections = fetchData.collections?.filter(c => c.type === 'newspaper-quizzes') ?? [];

  // Merge items from every collection of each type
  const polls   = pollsCollections.flatMap(c => c.collection_polls   ?? []);
  const quizzes = quizzesCollections.flatMap(c => c.collection_quizzes ?? []);

  // Use the first collection (if any) just for title/link
  const pollsTitle   = pollsCollections[0]?.collection_name ?? 'Polls';
  const pollsLink    = pollsCollections[0] ? `/newspaper/pq/${pollsCollections[0].collection_url}` : '';
  const quizzesTitle = quizzesCollections[0]?.collection_name ?? 'Quizzes';
  const quizzesLink  = quizzesCollections[0] ? `/newspaper/pq/${quizzesCollections[0].collection_url}` : '';

  // Build the ordered list of sections respecting the API order.
  const sections = [];
  let pollsQuizzesInserted = false;

  fetchData.collections?.forEach(collection => {
    // Insert the combined Polls/Quizzes slider at the position of the first
    // collection of either type. Skip any additional Poll/Quiz collections.
    if (['newspaper-polls', 'newspaper-quizzes'].includes(collection.type)) {
      if (!pollsQuizzesInserted && (polls.length > 0 || quizzes.length > 0)) {
        sections.push(
          {
            id: 'polls-quizzes-slider',
            content: (
              <PollsQuizzesSlider
                polls={polls}
                pollsTitle={pollsTitle}
                pollsLink={pollsLink}
                quizzes={quizzes}
                quizzesTitle={quizzesTitle}
                quizzesLink={quizzesLink}
              />
            ),
          }
        );
        pollsQuizzesInserted = true;
      }
      return;
    }

    sections.push({
      id: collection.collection_id,
      content: renderCollectionSection(collection),
    });
  });

  return (
    <>
      <StaticSeoHead
        title={newspaperSeo?.title || t('meta.title')}
        description={newspaperSeo?.metaDescription || t('meta.description')}
        canonicalUrl={newspaperSeo?.canonicalUrl}
        canonicalPath={newspaperSeo?.canonicalPath || '/newspaper'}
        robots={newspaperSeo?.robots}
        ogType={newspaperSeo?.ogType}
        image={newspaperSeo?.ogImage}
        pageType="CollectionPage"
        structuredData={newspaperSeo?.structuredData}
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Newspaper', url: 'https://suarte.art/newspaper' },
        ]}
      />
      <AppNavigationPage>
        <div className="newspaper__layout">
          <header className="newspaper__masthead">
            <h1 className="newspaper__title">{NEWSPAPER_MASTHEAD}</h1>
          </header>

          <aside className="newspaper__games-promo" aria-label={t('header.games_title')}>
            <Link to="/newspaper/art-games" className="newspaper__games-promo-link">
              <div className="newspaper__games-promo-content">
                <p className="newspaper__games-promo-title">{t('header.games_title')}</p>
                <p className="newspaper__games-promo-description">{t('header.games_subtitle')}</p>
              </div>
              <span className="newspaper__games-promo-arrow" aria-hidden="true">➜</span>
            </Link>
          </aside>

          <div className="newspaper__page">
            {sections.map(({ id, content }) => (
              <Fragment key={id}>{content}</Fragment>
            ))}
          </div>
        </div>
      </AppNavigationPage>
    </>
  );
}
